import React, { useEffect, useState, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import mqtt from "mqtt";
import {
  Button,
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Alert,
  CircularProgress,
  Divider,
  LinearProgress,
} from "@mui/material";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import NotificationsOffIcon from "@mui/icons-material/NotificationsOff";
import WarningIcon from "@mui/icons-material/Warning";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import HistoryIcon from "@mui/icons-material/History";
import PowerSettingsNewIcon from "@mui/icons-material/PowerSettingsNew";
import {
  sendAlarmCommand,
  fetchAlarmLogs,
  fetchActiveAlarms,
} from "./alarmSlice";

const Alarm = ({
  alarmId, // ADD THIS - database ID for API calls
  deviceId, // device ID for MQTT topics
  deviceName,
  cameraId,
  cameraName,
  userId,
  autoTrigger = false,
  showControls = true,
  onStateChange,
  mqttConfig = null,
  triggerDuration = 180,
}) => {
  const [client, setClient] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [alarmState, setAlarmState] = useState(false);
  const [ledState, setLedState] = useState(false);
  const [statusMessage, setStatusMessage] = useState("Connecting to MQTT...");
  const [autoTriggerTimer, setAutoTriggerTimer] = useState(null);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [showHistory, setShowHistory] = useState(false);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isAutoTriggeredFromBackend, setIsAutoTriggeredFromBackend] =
    useState(false);
  const [activeAlarmInfo, setActiveAlarmInfo] = useState(null);

  const dispatch = useDispatch();

  // Topic definitions
  const topicCmd = `fire/${deviceId}/cmd`;
  const topicState = `fire/${deviceId}/state`;

  // Check if this alarm is currently active (for auto-triggered alarms)
  useEffect(() => {
    const checkActiveAlarm = async () => {
      if (!alarmId) return;
      try {
        const result = await dispatch(fetchActiveAlarms({ alarm_id: alarmId }));
        const activeAlarms = result.payload?.results || [];
        if (activeAlarms.length > 0) {
          const active = activeAlarms[0];
          setActiveAlarmInfo(active);
          setAlarmState(true);
          setLedState(true);

          // Calculate remaining time
          const remaining = Math.max(
            0,
            Math.floor((new Date(active.auto_off_at) - new Date()) / 1000),
          );
          setTimeRemaining(remaining);

          // Check if this was auto-triggered
          if (active.triggered_by?.trigger_type?.startsWith("AUTO")) {
            setIsAutoTriggeredFromBackend(true);
            setStatusMessage(
              `🔴 AUTO-TRIGGERED - ${cameraName || deviceName || deviceId}`,
            );
          } else {
            setStatusMessage(
              `🔴 Alarm ON - ${cameraName || deviceName || deviceId}`,
            );
          }

          if (onStateChange) onStateChange(true);
        } else {
          setAlarmState(false);
          setLedState(false);
          setTimeRemaining(0);
          setActiveAlarmInfo(null);
          setIsAutoTriggeredFromBackend(false);
          if (onStateChange) onStateChange(false);
        }
      } catch (error) {
        console.error("Failed to check active alarm:", error);
      }
    };

    checkActiveAlarm();
    const interval = setInterval(checkActiveAlarm, 2000); // Poll every 2 seconds
    return () => clearInterval(interval);
  }, [alarmId, dispatch, cameraName, deviceName, deviceId, onStateChange]);

  // Connect to MQTT broker
  useEffect(() => {
    if (!deviceId) return;

    const mqttOptions = mqttConfig || {
      username: "hivemqtt",
      password: "Hivemqtt@1122",
      reconnectPeriod: 2000,
      connectTimeout: 10000,
      keepalive: 60,
      clean: true,
    };

    const mqttClient = mqtt.connect(
      mqttConfig?.host ||
        "wss://2be1374228c54154bc14422981467fff.s2.eu.hivemq.cloud:8884/mqtt",
      mqttOptions,
    );

    mqttClient.on("connect", () => {
      console.log(`✅ Connected to MQTT for device ${deviceId}`);
      setIsConnected(true);
      setStatusMessage(`Connected - ${cameraName || deviceName || deviceId}`);
      mqttClient.subscribe(topicState, (err) => {
        if (!err) {
          console.log(`Subscribed to ${topicState}`);
        }
      });
    });

    mqttClient.on("error", (err) => {
      console.error("MQTT Error:", err);
      setIsConnected(false);
      setStatusMessage("❌ MQTT Connection Error");
    });

    mqttClient.on("offline", () => {
      setIsConnected(false);
      setStatusMessage("📴 MQTT Offline");
    });

    mqttClient.on("message", (topic, message) => {
      const msg = message.toString();
      if (topic === topicState) {
        handleStateMessage(msg);
      }
    });

    setClient(mqttClient);

    return () => {
      if (autoTriggerTimer) {
        clearTimeout(autoTriggerTimer);
      }
      if (mqttClient) {
        mqttClient.end(true);
      }
    };
  }, [deviceId, cameraName, deviceName, mqttConfig]);

  // Timer for auto-off countdown
  useEffect(() => {
    let interval;
    if (alarmState && timeRemaining > 0) {
      interval = setInterval(() => {
        setTimeRemaining((prev) => {
          if (prev <= 1) {
            clearInterval(interval);
            setAlarmState(false);
            setLedState(false);
            setStatusMessage(
              `⚫ Alarm OFF - ${cameraName || deviceName || deviceId}`,
            );
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [alarmState, timeRemaining, cameraName, deviceName, deviceId]);

  // Handle incoming state messages from ESP
  const handleStateMessage = (message) => {
    if (message === "ALARM_ON") {
      setAlarmState(true);
      setLedState(true);
      setStatusMessage(`🔴 Alarm ON - ${cameraName || deviceName || deviceId}`);
      if (onStateChange) onStateChange(true);
    } else if (message === "ALARM_OFF") {
      setAlarmState(false);
      setLedState(false);
      setTimeRemaining(0);
      setStatusMessage(
        `⚫ Alarm OFF - ${cameraName || deviceName || deviceId}`,
      );
      if (onStateChange) onStateChange(false);
    }
  };

  // Send command to ESP
  const sendCommand = useCallback(
    async (command, duration = triggerDuration) => {
      if (!client || !isConnected) {
        setStatusMessage("❌ Cannot send command: MQTT not connected");
        return false;
      }

      try {
        client.publish(topicCmd, command);
        setStatusMessage(
          `📤 Sent: ${command} to ${cameraName || deviceName || deviceId}`,
        );

        // Use alarmId (database ID) for API call, not deviceId
        const result = await dispatch(
          sendAlarmCommand({
            id: alarmId, // IMPORTANT: Use database ID, not deviceId
            deviceId: deviceId, // Still pass deviceId for reference
            command,
            duration: command === "ON" ? duration : undefined,
            cameraId,
            eventUuid: null,
            triggerType: command === "ON" ? "MANUAL_TEST" : "MANUAL_OFF",
          }),
        ).unwrap();

        if (command === "ON") {
          setTimeRemaining(duration);
          setAlarmState(true);
          setLedState(true);
          setIsAutoTriggeredFromBackend(false);
        } else {
          setTimeRemaining(0);
          setAlarmState(false);
          setLedState(false);
        }

        return true;
      } catch (error) {
        console.error("Failed to send command:", error);
        setStatusMessage("❌ Failed to send command");
        return false;
      }
    },
    [
      client,
      isConnected,
      topicCmd,
      cameraName,
      deviceName,
      deviceId,
      alarmId,
      dispatch,
      cameraId,
      triggerDuration,
    ],
  );

  // Toggle alarm ON/OFF
  const toggleAlarm = () => {
    const newState = !alarmState;
    sendCommand(newState ? "ON" : "OFF");
  };

  // Trigger for specific duration
  const triggerForDuration = useCallback(
    (duration = triggerDuration) => {
      if (autoTriggerTimer) {
        clearTimeout(autoTriggerTimer);
      }

      sendCommand("ON", duration);

      const timer = setTimeout(() => {
        sendCommand("OFF");
        setStatusMessage(
          `⏰ Auto turn OFF after ${duration / 60} minutes - ${cameraName || deviceName || deviceId}`,
        );
      }, duration * 1000);

      setAutoTriggerTimer(timer);
    },
    [
      sendCommand,
      cameraName,
      deviceName,
      deviceId,
      triggerDuration,
      autoTriggerTimer,
    ],
  );

  // Auto-trigger when fire/smoke detected (for annotate or camera detection)
  useEffect(() => {
    if (autoTrigger && !alarmState) {
      triggerForDuration(triggerDuration);
    }

    return () => {
      if (autoTriggerTimer) {
        clearTimeout(autoTriggerTimer);
      }
    };
  }, [autoTrigger, triggerForDuration, triggerDuration, alarmState]);

  // Fetch alarm history
  const loadHistory = async () => {
    setLoading(true);
    const params = {};
    if (cameraId) params.camera_id = cameraId;
    if (alarmId) params.alarm_id = alarmId;

    const result = await dispatch(fetchAlarmLogs(params));
    setLogs(result.payload?.results || []);
    setLoading(false);
    setShowHistory(true);
  };

  // Format time (seconds to MM:SS)
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  // Format date
  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  return (
    <>
      <Card
        sx={{
          minWidth: 275,
          maxWidth: 400,
          background: alarmState
            ? "linear-gradient(145deg, #2a1a1a 0%, #1a1a1a 100%)"
            : "#1e1e1e",
          color: "white",
          border: alarmState ? "1px solid #ff4444" : "1px solid #333",
          boxShadow: alarmState ? "0 0 20px rgba(255,68,68,0.3)" : "none",
          transition: "all 0.3s ease",
          position: "relative",
          overflow: "visible",
        }}
      >
        {alarmState && (
          <Box
            sx={{
              position: "absolute",
              top: -10,
              right: -10,
              width: 20,
              height: 20,
              borderRadius: "50%",
              bgcolor: "#ff4444",
              animation: "pulse 1s infinite",
              "@keyframes pulse": {
                "0%": { transform: "scale(1)", opacity: 1 },
                "50%": { transform: "scale(1.5)", opacity: 0.7 },
                "100%": { transform: "scale(1)", opacity: 1 },
              },
            }}
          />
        )}

        <CardContent>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            mb={2}
          >
            <Box display="flex" alignItems="center" gap={1}>
              <WarningIcon sx={{ color: alarmState ? "#ff4444" : "#666" }} />
              <Typography variant="h6" component="div">
                {cameraName
                  ? `Alarm - ${cameraName}`
                  : deviceName || `Device ${deviceId}`}
              </Typography>
            </Box>
            <Box display="flex" alignItems="center" gap={1}>
              <Chip
                icon={isConnected ? <CheckCircleIcon /> : <ErrorIcon />}
                label={isConnected ? "Connected" : "Disconnected"}
                size="small"
                color={isConnected ? "success" : "error"}
                variant="outlined"
                sx={{
                  color: "white",
                  borderColor: isConnected ? "#4caf50" : "#f44336",
                }}
              />
              <Tooltip title="View History">
                <IconButton
                  size="small"
                  onClick={loadHistory}
                  sx={{ color: "#888" }}
                >
                  <HistoryIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          {/* Auto-triggered badge */}
          {isAutoTriggeredFromBackend && alarmState && (
            <Alert
              severity="warning"
              sx={{
                mb: 2,
                bgcolor: "#ff4444",
                color: "white",
                borderRadius: "8px",
                "& .MuiAlert-icon": { color: "white" },
              }}
            >
              ⚡ Auto-triggered by fire/smoke detection
            </Alert>
          )}

          {/* LED Indicator */}
          <Box display="flex" justifyContent="center" mb={2}>
            <Box
              sx={{
                width: 60,
                height: 60,
                borderRadius: "50%",
                bgcolor: ledState ? "#ff4444" : "#333",
                boxShadow: ledState ? "0 0 30px #ff4444" : "none",
                transition: "all 0.3s ease",
                animation: ledState ? "pulse 1.5s infinite" : "none",
                "@keyframes pulse": {
                  "0%": { opacity: 1, transform: "scale(1)" },
                  "50%": { opacity: 0.8, transform: "scale(1.1)" },
                  "100%": { opacity: 1, transform: "scale(1)" },
                },
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {alarmState && (
                <PowerSettingsNewIcon sx={{ color: "white", fontSize: 30 }} />
              )}
            </Box>
          </Box>

          {/* Timer Display */}
          {alarmState && timeRemaining > 0 && (
            <Box textAlign="center" mb={2}>
              <Typography
                variant="h3"
                sx={{
                  color: "#ff4444",
                  fontFamily: "monospace",
                  fontWeight: "bold",
                }}
              >
                {formatTime(timeRemaining)}
              </Typography>
              <Typography variant="caption" sx={{ color: "#888" }}>
                remaining
              </Typography>
              <LinearProgress
                variant="determinate"
                value={(timeRemaining / triggerDuration) * 100}
                sx={{
                  mt: 1,
                  height: 6,
                  borderRadius: 3,
                  bgcolor: "#333",
                  "& .MuiLinearProgress-bar": {
                    bgcolor: "#ff4444",
                  },
                }}
              />
            </Box>
          )}

          {/* Status Message */}
          <Typography
            variant="body2"
            sx={{
              mb: 2,
              color: "#bbb",
              textAlign: "center",
              fontSize: "0.9rem",
              minHeight: "40px",
            }}
          >
            {statusMessage}
          </Typography>

          {/* Device Info */}
          <Grid container spacing={1} mb={2}>
            <Grid item xs={6}>
              <Typography variant="caption" sx={{ color: "#888" }}>
                Device ID: {deviceId}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="caption" sx={{ color: "#888" }}>
                Camera ID: {cameraId || "N/A"}
              </Typography>
            </Grid>
          </Grid>

          <Divider sx={{ bgcolor: "#333", my: 2 }} />

          {/* Control Buttons */}
          {showControls && (
            <Box display="flex" justifyContent="center" gap={2} flexWrap="wrap">
              <Button
                variant="contained"
                onClick={toggleAlarm}
                disabled={!isConnected}
                startIcon={
                  alarmState ? (
                    <NotificationsOffIcon />
                  ) : (
                    <NotificationsActiveIcon />
                  )
                }
                sx={{
                  background: alarmState ? "#444" : "#ff4444",
                  "&:hover": {
                    background: alarmState ? "#555" : "#ff6666",
                  },
                  "&.Mui-disabled": {
                    background: "#333",
                    color: "#666",
                  },
                  minWidth: "120px",
                  fontWeight: "bold",
                }}
              >
                {alarmState ? "STOP ALARM" : "FIRE ALARM"}
              </Button>

              <Tooltip
                title={`Trigger alarm for ${triggerDuration / 60} minutes`}
              >
                <Button
                  variant="outlined"
                  onClick={() => triggerForDuration(triggerDuration)}
                  disabled={!isConnected || alarmState}
                  sx={{
                    borderColor: "#ff4444",
                    color: "#ff4444",
                    "&:hover": {
                      borderColor: "#ff6666",
                      backgroundColor: "rgba(255,68,68,0.1)",
                    },
                    "&.Mui-disabled": {
                      borderColor: "#666",
                      color: "#666",
                    },
                    minWidth: "120px",
                  }}
                >
                  {triggerDuration / 60} Min Test
                </Button>
              </Tooltip>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* History Dialog */}
      <Dialog
        open={showHistory}
        onClose={() => setShowHistory(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: "#1e1e1e",
            color: "white",
          },
        }}
      >
        <DialogTitle sx={{ borderBottom: "1px solid #333" }}>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <span>Alarm History - {cameraName || deviceName || deviceId}</span>
            {loading && (
              <CircularProgress size={20} sx={{ color: "#ff4444" }} />
            )}
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ bgcolor: "#1e1e1e", maxHeight: "400px" }}>
          {logs.length === 0 ? (
            <Typography sx={{ color: "#888", textAlign: "center", py: 4 }}>
              No alarm history found
            </Typography>
          ) : (
            logs.map((log, index) => (
              <Box
                key={index}
                sx={{
                  mb: 2,
                  pb: 2,
                  borderBottom:
                    index < logs.length - 1 ? "1px solid #333" : "none",
                  "&:hover": {
                    bgcolor: "#2a2a2a",
                  },
                }}
              >
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={3}>
                    <Typography variant="caption" sx={{ color: "#888" }}>
                      {formatDateTime(log.created_at)}
                    </Typography>
                  </Grid>
                  <Grid item xs={2}>
                    <Chip
                      label={log.trigger_type?.replace(/_/g, " ")}
                      size="small"
                      color={
                        log.trigger_type?.includes("FIRE")
                          ? "error"
                          : log.trigger_type?.includes("SMOKE")
                            ? "warning"
                            : "default"
                      }
                      sx={{
                        bgcolor: log.trigger_type?.includes("FIRE")
                          ? "#ff4444"
                          : log.trigger_type?.includes("SMOKE")
                            ? "#ff9800"
                            : "#333",
                        color: "white",
                      }}
                    />
                  </Grid>
                  <Grid item xs={1}>
                    <Typography
                      sx={{
                        color: log.command_sent === "ON" ? "#ff4444" : "#888",
                        fontWeight: "bold",
                      }}
                    >
                      {log.command_sent}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" sx={{ color: "#aaa" }}>
                      {log.notes || `${log.command_sent} command sent`}
                      {log.camera_name && ` - Camera: ${log.camera_name}`}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            ))
          )}
        </DialogContent>
        <DialogActions sx={{ borderTop: "1px solid #333", p: 2 }}>
          <Button
            onClick={() => setShowHistory(false)}
            variant="contained"
            sx={{
              bgcolor: "#ff4444",
              "&:hover": { bgcolor: "#ff6666" },
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default Alarm;
